import toMillis from './toMillis'
import { submissionKey } from '../dataHelpers/countSubmissions'
import inferIntervalDays from './inferIntervalDays'

/** Een respondent-id dat geen echte verwijzing is. `'undefined'` als tekst komt
 *  uit een URL-segment en is truthy — die glipt door elke falsy-controle heen.
 *  AI-CONTEXT: See docs/pitfalls.md#database-url-sentinel-string-persisted */
const isRespondentId = (value) =>
    typeof value === 'string' && value !== '' && value !== 'undefined' && value !== 'null'


/**
 * @typedef {object} ResearchAggregate
 * @property {Array<{respondentId: string, momentId: string, researchId: string, filledAtMs: number|null}>} fills
 * @property {Record<string, boolean>} anonymousByMoment - Per meetmoment: staan er naamloze antwoorden op?
 * @property {Record<string, number>} submissionsByMoment - Per meetmoment: hoeveel keer de
 *   vragenlijst is ingevuld, op naam of niet. Invullingen, NIET documenten: één ingevulde
 *   vragenlijst schrijft een document per vraag én per matrixrij, dus documenten tellen geeft
 *   een veelvoud. Bij een anoniem onderzoek is dit getal het enige dat te weten valt —
 *   'ingevuld door wie' bestaat daar niet.
 *   AI-CONTEXT: See docs/pitfalls.md#database-totaldocs-overcounts-respondents
 * @property {Record<string, number|null>} lastAnswerByMoment - Per meetmoment: wanneer het laatste
 *   antwoord binnenkwam, in milliseconden. `null` als er niets is, of als geen enkel antwoord een
 *   tijdstempel draagt — dat is iets anders dan 'geen antwoord'.
 * @property {boolean} hasIdentifiedAnswers - Staat er ook maar één antwoord op naam? Zo niet, dan
 *   hoort dit onderzoek op de kaart met anonieme onderzoeken en niet op die met deelnemers.
 * @property {number|null} interval - Afgeleide tussentijd in dagen
 */

/**
 * Wat er uit de antwoorden van ÉÉN onderzoek volgt.
 *
 * Deze afleiding is voor elke deelnemer van dat onderzoek dezelfde — de
 * tussentijd komt uit alle invullers samen en de anonimiteit is een eigenschap
 * van het meetmoment. Daarom staat hij los van de hook en kan hij gedeeld
 * worden tussen detailpagina's (docs/inbox.md #246).
 *
 * @param {object} params
 * @param {string} params.researchId - `Research.ID`
 * @param {Array<object>} params.responses - Levende `QuestionnairesResponses` van dit onderzoek
 * @param {Array<object>} params.moments - De `MeasureMoments` van dit onderzoek
 * @returns {ResearchAggregate}
 */
const deriveResearchAggregate = ({ researchId, responses, moments }) => {
    /** @type {Record<string, boolean>} */
    const anonymousByMoment = {}

    /** @type {Record<string, Set<string>>} */
    const submissionsPerMoment = {}
    /** @type {Record<string, number|null>} */
    const lastAnswerByMoment = {}

    // Elk meetmoment krijgt een waarde, ook zonder antwoorden: dat is juist het
    // geval waarin afwezigheid het zekerst is. Ontbreekt de sleutel, dan komt
    // het scherm op 'niet te zeggen' uit.
    ;(moments || []).forEach((moment) => {
        if (!moment.ID) return
        anonymousByMoment[moment.ID] = false
        submissionsPerMoment[moment.ID] = new Set()
        lastAnswerByMoment[moment.ID] = null
    })

    const fills = []
    ;(responses || []).forEach((response, index) => {
        if (!response.MomentID) return

        const answeredAtMs = toMillis(response.Timestamp)

        // Tellen gebeurt vóór de splitsing op naam: een anoniem antwoord is een
        // antwoord. Voor een onderzoek zonder herleidbare invullers is dit
        // getal het enige signaal dat er is. Geteld worden INVULLINGEN, want
        // één ingevulde vragenlijst levert een document per vraag en per
        // matrixrij op.
        if (!submissionsPerMoment[response.MomentID]) submissionsPerMoment[response.MomentID] = new Set()
        submissionsPerMoment[response.MomentID].add(submissionKey(response, answeredAtMs, index))
        if (answeredAtMs !== null
            && (!lastAnswerByMoment[response.MomentID] || answeredAtMs > lastAnswerByMoment[response.MomentID])) {
            lastAnswerByMoment[response.MomentID] = answeredAtMs
        }

        if (!isRespondentId(response.Persona)) {
            anonymousByMoment[response.MomentID] = true
            return
        }
        fills.push({
            respondentId: response.Persona,
            momentId: response.MomentID,
            researchId,
            filledAtMs: answeredAtMs,
        })
    })

    /** @type {Record<string, number>} */
    const submissionsByMoment = {}
    Object.keys(submissionsPerMoment).forEach((momentId) => {
        submissionsByMoment[momentId] = submissionsPerMoment[momentId].size
    })

    return {
        fills,
        anonymousByMoment,
        submissionsByMoment,
        lastAnswerByMoment,
        hasIdentifiedAnswers: fills.length > 0,
        interval: inferIntervalDays(fills),
    }
}

export default deriveResearchAggregate
export { isRespondentId }
